/**
 * L'identité Funo s'applique après les styles de chaque écran.
 * Référence : brand/funo/GUIDE-DE-MARQUE.md (v1.0, 24 septembre 2026).
 * Prune sourire porte le texte, les liens et les actions ; Violet arche et
 * Jaune porte restent des accents, jamais du petit texte sur blanc.
 */
export const CSS_BRAND_ADMIN = `
@font-face { font-family: "Fredoka"; font-style: normal; font-weight: 500 600; font-display: swap; src: url(/brand/polices/fredoka-latin.woff2) format("woff2"); }
@font-face { font-family: "Nunito Sans"; font-style: normal; font-weight: 400 800; font-display: swap; src: url(/brand/polices/nunito-sans-latin.woff2) format("woff2"); }
:root {
  --funo-violet: #787AF5;
  --funo-jaune: #F5BD64;
  --funo-prune: #391D44;
  --funo-encre: #2A1632;
  --funo-lavande: #E9DFF0;
  --funo-lavande-douce: #F6F1F9;
  --funo-creme: #FEFBF7;
  --funo-brume: #E7DDE9;
  --funo-gris: #6B5A70;
  --funo-bordure-champ: #87788E;
  --funo-bordure-secondaire: #CDBED8;
  --funo-jaune-doux: #FCEBCB;
  --funo-titres: "Fredoka", "Arial Rounded MT Bold", system-ui, sans-serif;
  --funo-texte: "Nunito Sans", Inter, system-ui, sans-serif;
  --admin-encre: var(--funo-encre);
  --admin-doux: var(--funo-gris);
  --admin-fond: var(--funo-creme);
  --admin-ligne: var(--funo-brume);
  --admin-primaire: var(--funo-prune);
  --admin-fonce: var(--funo-encre);
  --admin-accent: var(--funo-lavande);
}
body { font-family: var(--funo-texte); }
button:focus-visible, a:focus-visible, summary:focus-visible, input:focus-visible,
select:focus-visible, textarea:focus-visible, [tabindex]:focus-visible {
  outline: 3px solid var(--funo-prune);
  outline-offset: 3px;
}
.ux-app { color: var(--funo-encre); background: var(--funo-creme); }
.ux-app .admin__lateral { background: #fff; border-color: var(--funo-brume); }

/* Marque : le logo officiel, jamais retapé. */
.ux-marque { display: grid; justify-items: start; gap: 6px; color: var(--funo-encre); text-decoration: none; }
.ux-logo { display: block; width: 136px; height: auto; max-width: 100%; }
.ux-marque-description { font-size: 11px; font-weight: 700; letter-spacing: .02em; color: var(--funo-gris); white-space: nowrap; }
.ux-login-logo { display: block; width: 168px; height: auto; margin: 0 0 24px; }
.admin__connexion { background: var(--funo-creme); }
.admin__connexion h1 { font-family: var(--funo-titres); font-weight: 600; color: var(--funo-prune); letter-spacing: -.01em; }
.admin__surtitre { color: var(--funo-gris); }

/* Titres : Fredoka pour les grands titres seulement. */
.ux-page-entete h1, .admin__section-entete h2, .ux-accueil h1, .ux-accueil h2, .ev-root h1, .cat-admin h2, .acces-pane h1 {
  font-family: var(--funo-titres); font-weight: 600; letter-spacing: -.01em;
}
.ux-page-entete h1 { color: var(--funo-prune); }
.ux-page-entete p, .ux-page-entete p.ux-date { color: var(--funo-gris); }
.ux-page-entete p.ux-date { font-weight: 700; }

/* Organisation et navigation. */
.ux-organisation { border-color: var(--funo-brume); }
.admin__avatar, .admin__avatar--petit, .ac-avatar { background: var(--funo-lavande); color: var(--funo-prune); }
.ux-nav-label, .ux-breadcrumb, .ux-breadcrumb>strong { color: var(--funo-gris); }
.admin__navigation button { color: var(--funo-gris); border-radius: 12px; }
.admin__navigation button:hover { background: var(--funo-lavande-douce); color: var(--funo-prune); }
.admin__navigation button[aria-current="page"] {
  background: var(--funo-lavande); color: var(--funo-prune); border-color: transparent; font-weight: 700; box-shadow: none;
}
.admin__navigation button[aria-current="page"] svg, .ux-assistant-lien>svg:first-child { color: var(--funo-prune); }
.admin__navigation .ux-nav-enfants button[aria-current] { background: var(--funo-lavande-douce); color: var(--funo-prune); font-weight: 700; }
.ux-nav-enfants { border-color: var(--funo-brume); }
.ux-badge { background: var(--funo-jaune); color: var(--funo-prune); font-weight: 700; }
.ux-assistant-lien, .ux-site-lien { border-color: var(--funo-brume); color: var(--funo-gris); }
.ux-app .admin__barre { background: #FEFBF7F0; border-color: var(--funo-brume); }
.ux-breadcrumb svg, .ux-recherche-bouton { color: var(--funo-gris); }
.ux-recherche-bouton kbd { background: var(--funo-lavande-douce); border-color: var(--funo-brume); color: var(--funo-gris); }

/* Boutons : primaire prune, secondaire blanc, accent jaune avec texte prune. */
.admin__primaire, .ac-button.primary, .ev-primaire, .cat-admin button.cat-primaire {
  min-height: 48px; background: var(--funo-prune); border-color: var(--funo-prune); color: #fff; border-radius: 999px;
}
.admin__primaire:hover:not(:disabled), .ac-button.primary:hover, .ev-primaire:hover, .cat-admin button.cat-primaire:hover:not(:disabled) {
  background: var(--funo-encre); border-color: var(--funo-encre); box-shadow: 0 5px 14px #391D4429;
}
.admin__secondaire, .ac-button, .ev-secondaire { border-color: var(--funo-bordure-secondaire); color: var(--funo-prune); background: #fff; }
.admin__secondaire:hover:not(:disabled), .ac-button:hover, .ev-secondaire:hover { background: var(--funo-lavande-douce); border-color: var(--funo-prune); }
.admin__accent { background: var(--funo-jaune); border-color: var(--funo-jaune); color: var(--funo-prune); }
.admin__texte-bouton, .ac-button.subtle, .ac-instructions summary { color: var(--funo-prune); }

/* Champs : libellé visible, bordure lisible, prune au focus. */
.admin__champ input:not([type="checkbox"]):not([type="radio"]), .admin__champ select,
.admin__champ textarea { border-color: var(--funo-bordure-champ); color: var(--funo-encre); border-radius: 8px; }
.admin__champ input:not([type="checkbox"]):not([type="radio"]):focus, .admin__champ select:focus,
.admin__champ textarea:focus { border-color: var(--funo-prune); }
.admin__champ label { color: var(--funo-encre); }
input[type="checkbox"], input[type="radio"] { accent-color: var(--funo-prune); }

/* Cartes et panneaux : blanc sur crème, rayon 16. */
.ux-resume .admin__stat { border-color: var(--funo-brume); border-radius: 16px; }
.ux-resume .admin__stat>strong { color: var(--funo-prune); }
.admin__panneau, .ac-panel, .ev-carte { border-color: var(--funo-brume); border-radius: 16px; box-shadow: 0 5px 22px #391D4409; }
.admin__section-entete, .admin__table th, .admin__table td { border-color: #EFE8F2; }
.admin__section-entete h2, .ux-evenement-description strong, .admin__nom-demande { color: var(--funo-prune); }
.ux-vignette { background: var(--funo-lavande); color: var(--funo-prune); }
.ux-vignette--1 { background: var(--funo-jaune-doux); color: var(--funo-prune); }
.ux-vignette--2 { background: #E6E7FD; color: var(--funo-prune); }
.ux-prochaine-etape { background: linear-gradient(145deg, var(--funo-lavande-douce), var(--funo-jaune-doux)); border-color: var(--funo-brume); }
.ux-icone-douce, .ux-prochaine-etape .ux-icone-douce { background: var(--funo-lavande); color: var(--funo-prune); }
.ux-surtitle, .ux-prochaine-etape>p:not(.ux-surtitle) { color: var(--funo-gris); }
.ux-prochaine-etape>button { color: var(--funo-prune); border-color: var(--funo-bordure-secondaire); background: #fff; }
.ux-segments, .ac-tabs { background: var(--funo-lavande-douce); border-color: var(--funo-brume); }
.ux-segments button[aria-pressed="true"], .ac-tabs button[aria-pressed="true"] { color: var(--funo-prune); border-color: var(--funo-bordure-secondaire); background: #fff; }

/* Calendrier. */
.admin__jour--aujourdhui { background: var(--funo-lavande-douce); }
.admin__evenement { background: var(--funo-lavande); border-color: #D9CBE5; border-left-color: var(--funo-violet); color: var(--funo-prune); }
button.admin__evenement:hover { background: #DDD0EA; }

/* Conversation, notifications et accès. */
.admin__bulle--responsable, .ux-decision-fiche { background: var(--funo-lavande); border-color: #D9CBE5; color: var(--funo-encre); }
.admin__icone-service { color: var(--funo-prune); }
.ux-brand { color: var(--funo-prune); }
.acces-pane { color: var(--funo-encre); font-family: inherit; }
.acces-pane p, .ac-muted { color: var(--funo-gris); }
.ac-group-mark, .ac-empty-symbol { color: var(--funo-prune); }
.ac-connect-intro { background: linear-gradient(120deg, #fff, var(--funo-lavande-douce)); }
.ac-connect-symbol { background: var(--funo-lavande); color: var(--funo-prune); }

/* Événements et parcours. */
.ev-root { --ev-bleu: var(--funo-prune); --ev-encre: var(--funo-encre); --ev-gris: var(--funo-gris); --ev-ligne: var(--funo-brume); font-family: inherit; }
.ev-root :is(button,a,input,select,textarea,summary):focus-visible { outline-color: var(--funo-prune); }
.ev-root input:focus, .ev-root textarea:focus, .ev-root select:focus { box-shadow: 0 0 0 3px #391D4420; }
.ev-pastille, .ev-etape-active, .ev-parcours-recap li:before { background: var(--funo-lavande); color: var(--funo-prune); }
.ev-article-source, .ev-demande-libre, .ev-test { background: var(--funo-lavande-douce); border-color: #D9CBE5; }
.ev-configuration>.ev-etapes { background: #FBF8FC; }
.ev-configuration .ev-etapes nav .ev-etape-active { box-shadow: 0 3px 12px #391D4420; }

/* Pied de page : Funo, une solution Harmonixus. */
.ux-footer { color: var(--funo-gris); }
.ux-footer strong { color: var(--funo-prune); font-weight: 700; }

@media (prefers-reduced-motion: reduce) { .ux-app * { scroll-behavior: auto; } }
`
